import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, FileText, Loader2, Send, Undo2, X } from 'lucide-react';
import { assessmentApi } from '../../services/assessmentApi';
import { openBlob } from './MemoTools';
import { formatDate } from './statusMeta';

const SURVEY_NAME = { 'Initial Assessment': 'initial survey', '12 Week Review': 'final survey' };

// Server timestamps are UTC; show them as the viewer's local date.
function localDate(iso) {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <dt className="w-32 shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-900">{children || '—'}</dd>
    </div>
  );
}

// "Submit for payment": a confirmation summarising what will be filed, then
// the submit itself. A possible duplicate report is shown and needs a second,
// explicit confirmation -- as on the Session Reports page.
export function SubmitPanel({ record, values, online, survey, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const upload = record.server?.memoUpload;
  const isIA = record.formType === 'Initial Assessment';
  const surveyDone = survey?.state === 'completed';

  const submit = async (confirmDuplicate = false) => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(confirmDuplicate);
      setOpen(false);
    } catch (err) {
      if (err.body?.duplicateWarning) setDuplicate(err.body);
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setDuplicate(null); setError(null); }} disabled={!online}
        title={online ? undefined : 'Submitting needs an internet connection'}
        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
        <Send className="h-4 w-4" /> Submit for payment
      </button>
      {!online && <p className="mt-2 text-xs text-slate-500">Submitting needs an internet connection.</p>}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Submit assessment">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-md sm:rounded-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Submit for payment</h2>
              <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600">
              This files the session report and the assessment, and emails the manager. The form can't be edited afterwards.
            </p>
            <dl className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200 px-3">
              <Row label="Student">{record.studentName}</Row>
              <Row label="Type">{record.formType}</Row>
              <Row label="Date">{formatDate(values['meta.assessmentDate'])}</Row>
              <Row label="Place of service">{values['meta.placeOfService']}</Row>
              <Row label="Session length">{values['meta.sessionLength'] ? `${values['meta.sessionLength']} minutes` : null}</Row>
              {record.therapistName && <Row label="Therapist">{record.therapistName}</Row>}
              {isIA && <Row label="Memo">{upload ? 'Uploaded' : 'Complete'}</Row>}
            </dl>

            {!surveyDone && (
              <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
                <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  The student hasn't completed the {SURVEY_NAME[record.formType] || 'survey'} yet.{' '}
                  {isIA
                    ? 'Students cannot begin lessons or therapy until the survey is complete.'
                    : 'Students cannot continue lessons until the survey is complete.'}
                  {' '}Please remind the student. This does not affect your payment.
                </span>
              </div>
            )}

            {duplicate && (
              <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-900 ring-1 ring-red-200">
                <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> Possible duplicate</p>
                <p className="mt-1">{duplicate.msg} Check Session Reports before submitting again.</p>
              </div>
            )}
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-700 hover:bg-slate-100">
                Cancel
              </button>
              <button type="button" onClick={() => submit(!!duplicate)} disabled={busy}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                  duplicate ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? 'Submitting…' : duplicate ? 'Submit anyway' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const FOLLOW_UP = {
  pending: { label: 'Survey outstanding', className: 'bg-amber-50 text-amber-800 ring-amber-200', icon: Clock },
  completed: { label: 'Survey completed', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: CheckCircle2 },
  overridden: { label: 'Survey marked done by a manager', className: 'bg-blue-50 text-blue-700 ring-blue-200', icon: ClipboardCheck },
};

export function SurveyFollowUpBadge({ survey }) {
  const meta = survey && FOLLOW_UP[survey.status];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.className}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

// A submitted form: where it was filed, its documents, and the survey
// follow-up -- which managers/admins can mark done when the student
// completed it by other means.
export function SubmittedPanel({ record, privileged, online, onDetail }) {
  const s = record.server || {};
  const survey = s.survey;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [overriding, setOverriding] = useState(false);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      onDetail(await fn());
      setOverriding(false);
      setNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm md:p-6">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Submitted
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {s.submittedAt && `Submitted ${new Date(s.submittedAt.endsWith('Z') ? s.submittedAt : `${s.submittedAt}Z`).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
        {s.submittedBy && ` by ${s.submittedBy}`}. This form can no longer be edited.{' '}
        <Link to="/dashboard/reports/view" className="text-blue-700 hover:underline">View in Session Reports</Link>
      </p>

      {(s.documents || []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {s.documents.map((d) => (
            <li key={d.documentId}>
              <button type="button" disabled={!online}
                onClick={() => openBlob(() => assessmentApi.document(d.documentId), d.fileName).catch((err) => setError(err.message))}
                className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline disabled:text-slate-400">
                <FileText className="h-4 w-4" /> {d.fileName}
                <span className="text-xs text-slate-500">({d.category === 'Student Memo' ? 'memo' : 'completed form'})</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {survey && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <SurveyFollowUpBadge survey={survey} />
            {survey.status === 'completed' && survey.completedAt && (
              <span className="text-xs text-slate-500">{localDate(survey.completedAt)}</span>
            )}
            {survey.status === 'overridden' && (
              <span className="text-xs text-slate-500">
                {survey.overriddenBy}{survey.overriddenAt && `, ${localDate(survey.overriddenAt)}`}
                {survey.overrideNote && ` · “${survey.overrideNote}”`}
              </span>
            )}
          </div>

          {privileged && survey.status === 'pending' && !overriding && (
            <button type="button" onClick={() => setOverriding(true)} disabled={!online}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <ClipboardCheck className="h-4 w-4" /> Mark survey as completed
            </button>
          )}
          {privileged && overriding && (
            <div className="mt-3 rounded-lg bg-slate-50 p-3">
              <label className="block text-sm font-medium text-slate-700">
                How was it completed? (optional)
                <input type="text" value={note} maxLength={500} autoComplete="off" onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Completed on paper at the session"
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </label>
              <div className="mt-2 flex gap-2">
                <button type="button" disabled={busy} onClick={() => act(() => assessmentApi.surveyOverride(record.uuid, note))}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                  Mark completed
                </button>
                <button type="button" onClick={() => setOverriding(false)} className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {privileged && survey.status === 'overridden' && (
            <button type="button" disabled={busy || !online} onClick={() => act(() => assessmentApi.removeSurveyOverride(record.uuid))}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50">
              <Undo2 className="h-4 w-4" /> Undo
            </button>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}
